/**
 * Determines the pages to be displayed in the pagination component.
 *
 * @param {number} totalPages - The total number of totalPages.
 * @param {number} maxVisiblePages - The maximum number of pages that can be navigated directly.
 * @param {number} currentPage - The current page number.
 * @returns {Object} An object containing the start page, previous pages, visible pages, next pages, and end page.
 */

export const pagination = (totalPages = 0, maxVisiblePages = 5, currentPage = 1) => {
  /**
   *
   * @param {boolean} hasPagesOtherThanFirstAndLast - Indicates if there are pages other than the first and last page.
   * @param {boolean} shouldEllipse - Indicates if the pagination should display ellipses for hidden pages.
   * @param {boolean} shouldEllipsePrevious - Indicates if the pagination should display an ellipse before the current page.
   * @param {boolean} shouldEllipseNext - Indicates if the pagination should display an ellipse after the current page.
   */

  const shouldEllipse = totalPages > maxVisiblePages - 2 && totalPages !== maxVisiblePages;
  const shouldEllipsePrevious = shouldEllipse && currentPage > maxVisiblePages - 2;
  const shouldEllipseNext = shouldEllipse && totalPages - currentPage > maxVisiblePages - 2;
  const hasPagesOtherThanFirstAndLast = totalPages > 2;
  const startPage = 2; // 1 is fixed

  /**
   * Generates an array of page numbers from start to end.
   *
   * @param {number} start - The first page number.
   * @param {number} end - The last page number.
   * @returns {number[]} An array of page numbers.
   */
  const generatePages = (start, end) =>
    Array(end - start)
      .fill()
      .map((_, i) => i + start);

  let previousPages = [];
  let visiblePages = [];
  let nextPages = [];

  if (hasPagesOtherThanFirstAndLast) {
    if (!shouldEllipse) {
      visiblePages = generatePages(startPage, totalPages);
    } else if (!shouldEllipsePrevious) {
      // Generate all pages from 2 to maxVisiblePages
      visiblePages = generatePages(startPage, maxVisiblePages);

      // Generate nextPages dropdown from last item in visiblePages to totalPages
      const generatedNextPages = generatePages(visiblePages.slice(-1)[0] + 1, totalPages);

      // Only show nextPages dropdown if there is more than one item
      if (generatedNextPages.length > 1) {
        nextPages = generatedNextPages;
      } else {
        // Empty dropdown
        nextPages = [];

        const toPage = maxVisiblePages + 1; // need to add 1 because we need to include the item we removed from nextPages

        // Generate visiblePages
        visiblePages = generatePages(startPage, toPage);
      }
    } else if (shouldEllipseNext) {
      // Generate previousPages pages from 2 to the current page
      previousPages = generatePages(startPage, currentPage);

      // Only show previousPages dropdown if there is more than one item
      if (previousPages.length > 1) {
        const fromPage = currentPage;

        // Generate visiblePages
        const generatedVisiblePages = generatePages(
          fromPage,
          // discounting the first and the last page from maxVisiblePages, which are fixed values.
          currentPage + (maxVisiblePages - 2)
        );

        let toPage;

        // Check if the last item in generatedVisiblePages is equal to the totalPages - 2.
        if (generatedVisiblePages.slice(-1)[0] === totalPages - 2) {
          //  Need to add + 1 to calculate the visiblePages, since we don't show ellipse with 1 item. So instead of removing 2 (fixed first and last), we remove only 1, so toPages gets an extra page.
          toPage = currentPage + (maxVisiblePages - 1);

          // Generate visiblePages
          visiblePages = generatePages(fromPage, toPage);
        } else {
          // Generate visiblePages
          visiblePages = generatedVisiblePages;
        }
      } else {
        // Empty dropdown
        previousPages = [];

        const fromPage = currentPage - 1;
        const toPage = currentPage + (maxVisiblePages - 3); // besides discounting the first and the last page from maxVisiblePages, we also discount the item we removed from previousPages.

        // Generate visiblePages
        visiblePages = generatePages(fromPage, toPage);
      }

      // Generate nextPages dropdown from last item in visiblePages to totalPages
      const generatedNextPages = generatePages(visiblePages.slice(-1)[0] + 1, totalPages);

      // Only show nextPages dropdown if there is more than one item
      if (generatedNextPages.length > 1) {
        nextPages = generatedNextPages;
      }
    } else {
      /* Only shows previousPages dropdown */

      // adding the first page and the last page, that are already visible to totalPages
      const fromPage = totalPages - maxVisiblePages + 2;
      const toPage = totalPages;

      // Generate visiblePages
      visiblePages = generatePages(fromPage, toPage);

      // Generate previousPages
      previousPages = generatePages(startPage, fromPage);
    }
  }

  return {
    start: totalPages >= 1 ? 1 : null,
    previousPages,
    visiblePages,
    nextPages,
    end: totalPages >= 1 ? totalPages : null
  };
};
